import type {JsonValue} from 'type-fest';
import * as z from 'zod';

import {imageFromSanityImage} from '@/modules/foundation/sanity/image/query';

import {
	embroideryConfigurationFromFormData,
	embroideryConfigurationSchema,
	filterOptionEmbroideriesByBrandIds,
	getConfiguredEmbroidery,
	getDefaultEmbroideryConfiguration,
	getItemsForEmbroideryConfiguration,
} from '../../embroidery-collection/configuration';
import type {ItemWithVatTypeFragment} from '../../item/query';
import type {ProductAdapter} from '../adapter';
import type {ProductDescription} from '../description';
import {type ProductImageSide} from '../ProductImage';
import type {Overlay} from '../ProductImageStack';
import {getOverlayPlacement, type ProductImageStackDescription} from '../ProductImageStack';
import type {UnionProductFragment} from '../query';

import {DOCUMENT_TYPE_CAB_SHOE_TRAY_PRODDUCT} from './schema/names';
import {
	getBaseCabShoeTrayProductBrandsById,
	getFiltersMatchingCabShoeTrayProduct,
} from './filtering';
import type {CabShoeTrayProductFragment} from './query';

export const cabShoeTrayConfigurationSchema = z.object({
	embroidery: embroideryConfigurationSchema.optional(),
});

export type CabShoeTrayConfiguration = z.infer<typeof cabShoeTrayConfigurationSchema>;

export function parseCabShoeTrayConfiguration(value: unknown): CabShoeTrayConfiguration | null {
	const result = cabShoeTrayConfigurationSchema.safeParse(value);

	if (!result.success) {
		console.warn('Failed to parse cab shoe tray product configuration', result.error);
		return null;
	}

	return result.data;
}

export function isCabShoeTrayProduct(
	product: UnionProductFragment,
): product is CabShoeTrayProductFragment {
	return product._type === DOCUMENT_TYPE_CAB_SHOE_TRAY_PRODDUCT;
}

export function getFilteredEmbroideryOptionForCabShoeTray(product: CabShoeTrayProductFragment) {
	if (!product.settings?.embroidery) {
		return null;
	}

	// Filter embroideries to only those matching the product's brands:
	const allowedBrandIds = Array.from(getBaseCabShoeTrayProductBrandsById(product).keys());
	return filterOptionEmbroideriesByBrandIds(product.settings.embroidery, allowedBrandIds);
}

// Functions required for product page:

/**
 * Returns the simplest (but still valid) configuration of the product.
 */
export function getDefaultCabShoeTrayConfiguration(
	product: CabShoeTrayProductFragment,
): CabShoeTrayConfiguration {
	const defaultConfig: CabShoeTrayConfiguration = {};

	if (!product.settings) {
		return defaultConfig;
	}

	const embroidery = getFilteredEmbroideryOptionForCabShoeTray(product);
	if (embroidery?.available) {
		defaultConfig.embroidery = getDefaultEmbroideryConfiguration(embroidery);
	}

	// FIXME: Implement!

	return defaultConfig;
}

export function cabShoeTrayConfigurationFromFormData(
	product: CabShoeTrayProductFragment,
	formData: FormData,
): CabShoeTrayConfiguration {
	const config: CabShoeTrayConfiguration = {};

	const embroidery = getFilteredEmbroideryOptionForCabShoeTray(product);
	if (embroidery?.available) {
		config.embroidery = embroideryConfigurationFromFormData(embroidery, 'embroidery', formData);
	}

	return config;
}

export function cabShoeTrayConfigurationFromSearchParams(
	product: CabShoeTrayProductFragment,
	searchParams: URLSearchParams,
): CabShoeTrayConfiguration {
	const config: CabShoeTrayConfiguration = {};

	// TODO: Implement all conversion to/from search params like this at the top level and remove the type specific conversion functions.

	const param = searchParams.get('configuration');
	if (!param) {
		return config;
	}

	let jsonConfig: JsonValue = null;
	try {
		jsonConfig = JSON.parse(atob(param));
	} catch (e) {
		console.warn('Failed to parse cab table configuration from search params', e);
	}

	if (!jsonConfig) {
		return config;
	}

	const parseResult = cabShoeTrayConfigurationSchema.safeParse(jsonConfig);
	if (!parseResult.success) {
		return config;
	}

	return parseResult.data;
}

export function getItemsForCabShoeTrayConfiguration(
	product: CabShoeTrayProductFragment,
	configuration: CabShoeTrayConfiguration,
): ItemWithVatTypeFragment[] {
	const items: ItemWithVatTypeFragment[] = [];

	// Add base product items:
	items.push(...(product.settings?.items ?? []));

	// Add configured option items:
	const embroidery = getFilteredEmbroideryOptionForCabShoeTray(product);
	if (embroidery?.available && configuration.embroidery) {
		const embroideryItems = getItemsForEmbroideryConfiguration(
			embroidery,
			configuration.embroidery,
		);
		items.push(...embroideryItems);
	}

	// FIXME: Get configured option items

	return items;
}

export type CabShoeTrayConfigurationValidationResult = {
	isValid: boolean;
};

export function validateCabShoeTrayConfiguration(
	_product: CabShoeTrayProductFragment,
	_configuration: CabShoeTrayConfiguration,
): CabShoeTrayConfigurationValidationResult {
	// FIXME: Implement!

	return {isValid: true};
}

export function isValidCabShoeTrayConfiguration(
	product: CabShoeTrayProductFragment,
	configuration: CabShoeTrayConfiguration,
) {
	const validation = validateCabShoeTrayConfiguration(product, configuration);
	return validation.isValid;
}

export function getImageStackForCabShoeTrayConfiguration<Side extends ProductImageSide>(
	side: Side,
	product: CabShoeTrayProductFragment,
	configuration: CabShoeTrayConfiguration | null,
): ProductImageStackDescription | null {
	if (side !== 'top') {
		// For cab shoe trays, we only have a top image:
		return null;
	}

	if (!product.settings) {
		return null;
	}

	// Fetch the most specific (enabled) product image settings:
	const imageSettings = product.productImageSettings?.enabled
		? product.productImageSettings
		: product.settings.productImageSettings?.enabled
			? product.settings.productImageSettings
			: null;

	if (!imageSettings) {
		return null;
	}

	const baseImage = imageFromSanityImage(imageSettings.baseImage);

	if (!baseImage) {
		// If there's no base image, we don't need to consider the overlays either:
		return null;
	}

	const overlays: Overlay[] = [];

	// Add embroidery overlay, if configured:
	const embroidery = getFilteredEmbroideryOptionForCabShoeTray(product);
	if (embroidery?.available && configuration?.embroidery) {
		const placement = imageSettings.embroideryImagePlacement;

		const {configuredEmbroidery} = getConfiguredEmbroidery(
			embroidery,
			configuration.embroidery,
		);

		if (placement?.isVisible && configuredEmbroidery?.image) {
			const overlayImage = imageFromSanityImage(configuredEmbroidery.image);
			if (overlayImage) {
				overlays.push({
					_type: 'imageOverlay',
					image: overlayImage,
					placement: getOverlayPlacement(placement),
				});
			}
		}
	}

	return {
		image: baseImage,
		altText: null,
		overlays,
	};
}

// Functions required for category pages and cards

export function getProductNameForCabShoeTrayConfiguration(
	product: CabShoeTrayProductFragment,
	_configuration: CabShoeTrayConfiguration,
) {
	return `${product.name}`;
}

export function getDescriptionForCabShoeTrayConfiguration(
	product: CabShoeTrayProductFragment,
	configuration: CabShoeTrayConfiguration,
): ProductDescription {
	const desc: ProductDescription = [];

	const embroidery = getFilteredEmbroideryOptionForCabShoeTray(product);

	if (embroidery?.available && configuration.embroidery) {
		const {configuredCollection, configuredEmbroidery} = getConfiguredEmbroidery(
			embroidery,
			configuration.embroidery,
		);

		if (configuredCollection && configuredEmbroidery) {
			desc.push({
				title: 'Broderi',
				value: [
					{
						title: 'Broderisamling',
						value: configuredCollection.name ?? 'Samling uten navn',
					},
					{
						title: 'Navn',
						value: configuredEmbroidery.name ?? 'Broderi uten navn',
					},
				],
			});
		}
	}

	return desc;
}

export const cabShoeTrayProductAdapter: ProductAdapter<CabShoeTrayProductFragment> = {
	getName: getProductNameForCabShoeTrayConfiguration,
	getItems: getItemsForCabShoeTrayConfiguration,
	getImages: getImageStackForCabShoeTrayConfiguration,
	getDescription: getDescriptionForCabShoeTrayConfiguration,
	getMatchingFilters: getFiltersMatchingCabShoeTrayProduct,
	isValidConfig: isValidCabShoeTrayConfiguration,
	getDefaultConfig: getDefaultCabShoeTrayConfiguration,
	parseConfigFromFormData: cabShoeTrayConfigurationFromFormData,
	parseConfigFromJSON: parseCabShoeTrayConfiguration,
};
